// src/contexts/GuestContext.jsx
/**
 * GuestContext
 *
 * Manages "browse as guest" mode — a frontend-only flag stored in sessionStorage.
 * Clears automatically when the tab closes (sessionStorage lifetime).
 *
 * Usage:
 *   const { isGuest, enterGuestMode, exitGuestMode } = useGuest();
 *
 * What guests CAN do:
 *   - View competitions and submissions
 *   - View Hall of Fame (leaderboard, winners)
 *   - View events / calendar
 *
 * What guests CANNOT do:
 *   - Vote on submissions
 *   - Submit to competitions
 *   - Access /profile, /me, or any auth-required route
 *
 * No backend involvement — all read endpoints are IsAuthenticatedOrReadOnly,
 * so unauthenticated requests already work fine.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const GuestContext = createContext(null);

const GUEST_KEY = 'zelospace_guest_mode';

export const GuestProvider = ({ children }) => {
  const [isGuest, setIsGuest] = useState(
    () => sessionStorage.getItem(GUEST_KEY) === 'true'
  );

  const enterGuestMode = useCallback(() => {
    sessionStorage.setItem(GUEST_KEY, 'true');
    setIsGuest(true);
  }, []);

  const exitGuestMode = useCallback(() => {
    sessionStorage.removeItem(GUEST_KEY);
    setIsGuest(false);
  }, []);

  return (
    <GuestContext.Provider value={{ isGuest, enterGuestMode, exitGuestMode }}>
      {children}
    </GuestContext.Provider>
  );
};

export const useGuest = () => useContext(GuestContext);